import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { ApprovalState, ApprovalType } from '@mg/shared';
import type { Post } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { useEffectiveApprovalType } from '../../lib/useEffectiveApprovalType';
import { approvalStateLabel } from '../../lib/display';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormField, Select } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { ErrorState, LoadingState } from '../../components/ui/States';

/** Which post action the picker is serving. */
export type CampaignPickerAction = 'duplicate' | 'move';

/** Sentinel for "No campaign — one-off post": sends no `campaignId` → the One-Off Posts bucket. */
const ONE_OFF = '__one_off__';

const EDITABLE_STATES = new Set<ApprovalState>([ApprovalState.DRAFT, ApprovalState.REVISE]);
const CLOSED_STATES = new Set<ApprovalState>([ApprovalState.PUBLISHED, ApprovalState.REJECTED]);

/**
 * Why a campaign can't take this post — mirrors the server's `assertCampaignAcceptsNewPost`:
 * CAMPAIGN mode only accepts DRAFT/REVISE campaigns; POST mode accepts anything that isn't
 * terminal (published/rejected); NONE is never gated. `null` = eligible destination.
 */
function blockedReason(state: ApprovalState, approvalType: ApprovalType): string | null {
  if (approvalType === ApprovalType.CAMPAIGN) return EDITABLE_STATES.has(state) ? null : 'locked';
  if (approvalType === ApprovalType.POST) return CLOSED_STATES.has(state) ? 'closed' : null;
  return null;
}

interface DestinationOption {
  id: string;
  name: string;
  state: ApprovalState;
  /** Short "why not" suffix, or null when the campaign can take the post. */
  blocked: string | null;
}

/**
 * One modal for both post-level campaign moves: "Duplicate post" (any status — the server always
 * creates a fresh DRAFT copy) and "Move to campaign" (drafts only). Ineligible destinations are
 * DISABLED rather than hidden, so the rule is discoverable instead of producing a guaranteed 400.
 */
export function CampaignPickerModal({
  open,
  action,
  post,
  onClose,
  onDone,
}: {
  open: boolean;
  action: CampaignPickerAction;
  /** The source post being copied / re-parented. */
  post: Post;
  onClose: () => void;
  /** Fired with the server's result before the modal closes (caller invalidates). */
  onDone: (result: Post) => void;
}) {
  const tenantKey = useTenantKey();
  // Effective mode (the install-wide global override folds in) — same source the server uses,
  // so the eligibility rules below never diverge from what the API enforces.
  const approvalType = useEffectiveApprovalType();
  const fieldId = useFieldId('picker-campaign');
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isDuplicate = action === 'duplicate';

  const campaignsQ = useQuery({
    queryKey: ['campaigns', tenantKey],
    queryFn: ({ signal }) => api.listCampaigns(signal),
    enabled: open && tenantKey !== '',
  });
  const campaigns = useMemo(() => campaignsQ.data ?? [], [campaignsQ.data]);

  /** Does the post already live in the auto-created one-off bucket? */
  const currentIsOneOff = useMemo(
    () => campaigns.some((c) => c.id === post.campaignId && c.isOneOff),
    [campaigns, post.campaignId],
  );

  // Moving to the campaign the post is already in is a no-op — drop it from the list entirely.
  // The one-off bucket is a real campaign row but is presented ONCE, as the "no campaign"
  // sentinel below; listing it here too would offer the very same destination twice.
  const options: DestinationOption[] = useMemo(
    () =>
      campaigns
        .filter((c) => !c.isOneOff)
        .filter((c) => isDuplicate || c.id !== post.campaignId)
        .map((c) => ({
          id: c.id,
          name: c.name,
          state: c.approvalState,
          blocked: blockedReason(c.approvalState, approvalType),
        })),
    [campaigns, isDuplicate, post.campaignId, approvalType],
  );

  // A campaign-less post is rejected while approvals sit at the Campaign level (server:
  // createPost/duplicate both refuse it), so don't offer the bucket in that mode. For a MOVE,
  // also hide it when the post is ALREADY one-off: the server would resolve the same bucket and
  // no-op, so an enabled "Move post" would close the modal reporting a move that never happened
  // (and in a workspace whose only campaign is the bucket it would be the sole option).
  const oneOffAllowed =
    approvalType !== ApprovalType.CAMPAIGN && !(!isDuplicate && currentIsOneOff);

  // Derived (not stored) so a background campaigns refetch can never stomp the user's choice.
  const defaultValue = useMemo(() => {
    const eligible = options.filter((o) => o.blocked == null);
    if (isDuplicate) {
      // Default to "where the post already lives" so a copy stays put unless the user says
      // otherwise. The one-off bucket is no longer in `options` (it IS the sentinel), so a
      // one-off post has to match on the sentinel — without this a copy would silently default
      // into some other campaign, and in POST mode reopen it for review.
      if (currentIsOneOff && oneOffAllowed) return ONE_OFF;
      const current = eligible.find((o) => o.id === post.campaignId);
      if (current) return current.id;
    }
    if (eligible.length > 0) return eligible[0].id;
    return oneOffAllowed ? ONE_OFF : '';
  }, [options, isDuplicate, post.campaignId, oneOffAllowed, currentIsOneOff]);

  // Fall back to the default if a refetch made the picked destination disappear or become
  // ineligible, so the <Select> never points at an option that isn't there.
  const selected =
    value != null &&
    (value === ONE_OFF ? oneOffAllowed : options.some((o) => o.id === value && o.blocked == null))
      ? value
      : defaultValue;
  const selectedOption = options.find((o) => o.id === selected) ?? null;
  const hasChoice = options.some((o) => o.blocked == null) || oneOffAllowed;
  const canConfirm =
    selected === ONE_OFF ? oneOffAllowed : selectedOption != null && selectedOption.blocked == null;

  // Real, verified server behaviour: in POST mode a campaign's status is DERIVED from its posts,
  // so dropping a fresh draft into a campaign that has moved past Draft pulls it back to Draft
  // (i.e. it must be reviewed again). Inform, never block.
  const reopensForReview =
    approvalType === ApprovalType.POST &&
    selectedOption != null &&
    selectedOption.blocked == null &&
    !EDITABLE_STATES.has(selectedOption.state);

  const mutation = useMutation({
    mutationFn: () => {
      const campaignId = selected === ONE_OFF ? undefined : selected;
      return isDuplicate
        ? api.duplicatePost(post.id, { campaignId })
        : api.movePost(post.id, { campaignId });
    },
    onSuccess: (result) => {
      onDone(result);
      onClose();
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError
          ? err.message
          : isDuplicate
            ? 'Could not duplicate the post.'
            : 'Could not move the post.',
      ),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isDuplicate ? 'Duplicate post' : 'Move to campaign'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={!canConfirm || campaignsQ.isLoading || campaignsQ.isError}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {isDuplicate ? 'Duplicate post' : 'Move post'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-ink-label">
            {isDuplicate
              ? 'Choose the campaign to add the copy to. The duplicate is created as a draft.'
              : 'Choose the campaign to move this post into.'}
          </p>
          <p className="mt-1 truncate text-sm font-medium text-ink">
            “{post.title || 'Untitled post'}”
          </p>
        </div>

        {campaignsQ.isLoading ? (
          <LoadingState label="Loading campaigns…" />
        ) : campaignsQ.isError ? (
          <ErrorState
            message={
              campaignsQ.error instanceof ApiRequestError
                ? campaignsQ.error.message
                : 'Could not load campaigns.'
            }
            onRetry={() => void campaignsQ.refetch()}
          />
        ) : !hasChoice ? (
          <div className="rounded-lg border border-line bg-page px-3 py-3">
            <p className="text-sm font-medium text-ink">
              {isDuplicate
                ? 'No campaign can take the copy right now.'
                : 'There’s nowhere to move this post right now.'}
            </p>
            <p className="mt-1 text-sm text-ink-muted">
              {approvalType === ApprovalType.CAMPAIGN
                ? 'While approvals are set at the Campaign level, only campaigns in Draft or Revise can accept posts. Create a new campaign, or ask for changes on an existing one.'
                : 'Every other campaign is closed (published or rejected). Create a new campaign to continue.'}
            </p>
          </div>
        ) : (
          <FormField label="Campaign" htmlFor={fieldId}>
            <Select
              id={fieldId}
              value={selected}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
            >
              {options.map((o) => (
                <option key={o.id} value={o.id} disabled={o.blocked != null}>
                  {o.name}
                  {o.blocked ? ` — ${o.blocked}` : ''}
                </option>
              ))}
              {oneOffAllowed && <option value={ONE_OFF}>🗒️ No campaign — one-off post</option>}
            </Select>
          </FormField>
        )}

        {reopensForReview && selectedOption && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-sm text-amber-800 dark:text-warning-dark">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Adding a draft to “{selectedOption.name}” ({approvalStateLabel[selectedOption.state]})
              reopens that campaign for review.
            </span>
          </p>
        )}

        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      </div>
    </Modal>
  );
}
